<script lang="ts">
  import BottomSheet from './BottomSheet.svelte';
  import type { Item } from '../lib/types';
  import { makeItem } from '../lib/defaults';

  interface Props {
    open: boolean;
    items: Item[];
    onsave: (items: Item[]) => void;
    onclose: () => void;
  }

  let { open, items, onsave, onclose }: Props = $props();

  let text = $state('');

  // open になるたびに現在の項目を反映
  $effect(() => {
    if (open) {
      text = items.map((i) => i.text).join('\n');
    }
  });

  function save(): void {
    const lines = text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    // 既存項目を text で照合し、ON/OFF・確率設定・分割数を引き継ぐ
    const usedIndexes = new Set<number>();
    const newItems: Item[] = lines.map((line) => {
      for (let i = 0; i < items.length; i++) {
        if (!usedIndexes.has(i) && items[i].text === line) {
          usedIndexes.add(i);
          return { ...items[i], text: line };
        }
      }
      return makeItem(line);
    });

    if (newItems.length === 0) {
      newItems.push(makeItem('項目 1'));
    }
    onsave(newItems);
    onclose();
  }

  const lineCount = $derived(
    text.split(/\r?\n/).filter((l) => l.trim().length > 0).length,
  );
</script>

<BottomSheet {open} title="項目編集" {onclose}>
  <p class="hint">1 行 1 項目で記入してください。空行は無視されます。</p>
  <textarea
    bind:value={text}
    rows="12"
    placeholder={'項目 1\n項目 2\n項目 3'}
    spellcheck="false"
    autocapitalize="off"
  ></textarea>
  <p class="counter">{lineCount} 項目</p>
  <div class="actions">
    <button class="cancel" onclick={onclose}>キャンセル</button>
    <button class="save" onclick={save} disabled={lineCount === 0}>保存</button>
  </div>
</BottomSheet>

<style>
  .hint {
    margin: 0 0 var(--space-sm);
    font-size: var(--font-size-sm);
    color: var(--color-fg-muted);
  }

  textarea {
    width: 100%;
    min-height: 220px;
    padding: var(--space-md);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    background: var(--color-bg);
    color: var(--color-fg);
    resize: vertical;
    font-family: inherit;
    font-size: var(--font-size-base);
    line-height: 1.55;
    user-select: text;
    -webkit-user-select: text;
  }

  textarea:focus {
    outline: 2px solid var(--color-accent);
    outline-offset: -1px;
  }

  .counter {
    margin: var(--space-sm) 0 0;
    text-align: right;
    font-size: 12px;
    color: var(--color-fg-muted);
  }

  .actions {
    display: flex;
    gap: var(--space-sm);
    margin-top: var(--space-md);
  }

  .actions button {
    flex: 1;
    padding: 12px;
    border-radius: var(--radius-sm);
    font-weight: 600;
    font-size: var(--font-size-base);
  }

  .actions button:disabled {
    opacity: 0.4;
  }

  .cancel {
    background: var(--color-border);
    color: var(--color-fg);
  }

  .save {
    background: var(--color-accent);
    color: var(--color-accent-fg);
  }

  .save:active {
    transform: scale(0.98);
  }
</style>
